import React, {useContext, useState} from "react";
import {Box, Button, ButtonBase, InputBase, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Search01Icon} from "@hugeicons/core-free-icons";
import {SignInContext} from "../../context/SignInContext";
import {controlSx, Panel} from "../registry-project/dashboard/components/Panel";
import {ApiKeyRepository} from "../../repositories/ApiKeyRepository";
import {ApiKeysTable} from "./components/api-keys-table";
import {useApiKeys} from "./useApiKeys";
import {canManageApiKey} from "./apiKeyAccess";

const headerButtonSx = {
  height: 38, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 700,
  color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
};

const SCOPE_OPTIONS = [
  {value: "my", label: "My keys"},
  {value: "all", label: "All keys"},
];

// Scope switch (segmented pill), same pattern as VersionTypeSwitch in registry-project/SubProjectSelect.js
const ScopeSwitch = ({value, onChange}) => (
  <Stack role={"radiogroup"} aria-label={"Scope"} direction={"row"} sx={{
    height: 38, p: 0.25, gap: 0.25, borderRadius: "999px", bgcolor: "#FAFAFD", flexShrink: 0,
  }}>
    {SCOPE_OPTIONS.map(option => {
      const selected = option.value === value;
      return (
        <ButtonBase
          key={option.value}
          role={"radio"}
          aria-checked={selected}
          onClick={() => !selected && onChange(option.value)}
          sx={{
            px: 1.5, borderRadius: "999px", fontSize: 13, fontWeight: 600,
            color: selected ? "#4B55C9" : "#6B6B80", bgcolor: selected ? "rgba(132, 145, 247, 0.14)" : "transparent",
            transition: "background-color .15s ease, color .15s ease",
            "&:hover": {color: selected ? "#4B55C9" : "#23264D", bgcolor: selected ? "rgba(132, 145, 247, 0.18)" : "#F4F4FA"},
          }}
        >
          {option.label}
        </ButtonBase>
      );
    })}
  </Stack>
);

export const ApiKeys = () => {
  const {currentUser, organizations} = useContext(SignInContext);
  const isAdmin = currentUser?.roles?.includes("admin");

  const [scope, setScope] = useState("my");
  const [search, setSearch] = useState("");

  const {apiKeys, openCreate, dialogs, tableProps} = useApiKeys({
    fetchKeys: async () => {
      const response = scope === "all" ? await ApiKeyRepository.getAll() : await ApiKeyRepository.getMyKeys();
      return response.data;
    },
    deps: [scope],
    organizations,
  });

  const query = search.trim().toLowerCase();
  const filteredKeys = query
    ? apiKeys.filter(apiKey => [apiKey.maskedKey, apiKey.username, apiKey.organizationName]
      .some(value => String(value || "").toLowerCase().includes(query)))
    : apiKeys;

  return (
    <>
      {dialogs}
      <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: 1200, width: "100%", mx: "auto", boxSizing: "border-box"}}>
        <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
          <Box>
            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>ACCOUNT</Typography>
            <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
              API Keys
            </Typography>
            <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
              Manage keys for accessing the OpenBrain API
            </Typography>
          </Box>
          <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
            <Box component={"label"} sx={{...controlSx, width: 220}}>
              <HugeiconsIcon icon={Search01Icon} size={16}/>
              <InputBase
                placeholder={"Search keys..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                inputProps={{"aria-label": "Search keys"}}
                sx={{flex: 1, fontSize: 13, color: "#23264D"}}
              />
            </Box>
            {isAdmin && <ScopeSwitch value={scope} onChange={setScope}/>}
            <Button
              sx={{...headerButtonSx, textWrap: "nowrap", flexShrink: 0}}
              startIcon={<HugeiconsIcon icon={Add01Icon} size={17}/>}
              onClick={openCreate}
            >
              Create Key
            </Button>
          </Stack>
        </Stack>

        <Panel sx={{overflow: "hidden"}}>
          <ApiKeysTable
            {...tableProps}
            apiKeys={filteredKeys}
            canManageKey={apiKey => canManageApiKey(apiKey, currentUser, organizations)}
            emptyText={apiKeys.length === 0 ? "No API keys created yet" : `No keys match "${search}"`}
          />
        </Panel>
      </Stack>
    </>
  );
};
